/**
 * CopilotCitationCard — shared expandable citation card used by both the
 * File Vault Copilot and the Workspace Copilot.
 *
 * Contract
 * - `citation` is either the File-Vault shape `{index, file_id, filename,
 *   excerpt, quote, chunk_index?}` or the Workspace shape `{index,
 *   source_kind, source_id, source_name, source_meta, excerpt, quote,
 *   chunk_index?, score?}`.
 * - `onOpen` is called with the citation object when the user clicks
 *   "Open source" (for File Vault: opens the file detail modal; for
 *   Workspace: navigates to the source entity/article/file).
 * - Clicking the card body toggles the in-place "See exact quote" expand,
 *   showing the retrieved chunk text with the LLM's supporting phrase
 *   highlighted in place.
 */
import React, { useState, useMemo } from 'react';
import {
  ArrowRight, ChevronDown, ChevronUp, FileText, Newspaper,
  Network as NetworkIcon, ExternalLink, LineChart, Bot, Layers,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// iter114.4 — extended with Board Briefing orchestrations.
const SOURCE_META = {
  file: { icon: FileText, label: 'File', color: 'text-rose-300 bg-rose-950/60 border-rose-800/50' },
  content: { icon: Newspaper, label: 'Article', color: 'text-amber-300 bg-amber-950/60 border-amber-800/50' },
  entity: { icon: NetworkIcon, label: 'Entity', color: 'text-violet-300 bg-violet-950/60 border-violet-800/50' },
  economist: { icon: LineChart, label: 'Economist', color: 'text-blue-300 bg-blue-950/60 border-blue-800/50' },
  agent: { icon: Bot, label: 'Agent', color: 'text-emerald-300 bg-emerald-950/60 border-emerald-800/50' },
  orchestration: { icon: Layers, label: 'Orchestration', color: 'text-indigo-300 bg-indigo-950/60 border-indigo-800/50' },
};

const escapeRegex = (s) => (s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Highlight one or more phrases inside an excerpt.
 *
 * Keys combine position + segment content so React reconciliation stays
 * stable across re-renders. Two segments with identical text at different
 * positions still get distinct keys via the leading index prefix.
 */
const highlightText = (text, phrases) => {
  const clean = (phrases || []).map((p) => (p || '').trim()).filter((p) => p.length >= 4);
  if (!text || clean.length === 0) return text;
  try {
    const pattern = new RegExp(`(${clean.map(escapeRegex).join('|')})`, 'ig');
    const parts = text.split(pattern);
    return parts.map((p, i) => {
      const key = `${i}:${p}`;
      return pattern.test(p)
        ? <mark key={key} className="bg-amber-500/25 text-amber-100 rounded px-0.5">{p}</mark>
        : <span key={key}>{p}</span>;
    });
  } catch {
    return text;
  }
};

const CopilotCitationCard = ({ citation, onOpen, kind = 'file' }) => {
  const [expanded, setExpanded] = useState(false);
  const effectiveKind = citation.source_kind || kind;
  const meta = SOURCE_META[effectiveKind] || SOURCE_META.file;
  const Icon = meta.icon;

  const label = citation.source_name || citation.filename || 'Untitled';
  const excerpt = citation.excerpt || '';
  const quote = citation.quote || '';

  const highlighted = useMemo(() => highlightText(excerpt, [quote]), [excerpt, quote]);

  const meta_url = citation.source_meta?.url;
  const meta_signal = citation.source_meta?.signal_id;
  const meta_entity_type = citation.source_meta?.entity_type;

  const handleToggle = () => {
    if (excerpt) setExpanded((v) => !v);
  };

  return (
    <div
      className="rounded bg-[#0F1730] border border-[#1F2A4D] hover:border-rose-800/50 hover:bg-[#131C3A] transition"
      data-testid={`citation-card-${effectiveKind}-${citation.source_id || citation.file_id}`}
    >
      <button
        type="button"
        onClick={handleToggle}
        className="w-full text-left p-2 flex items-center gap-2"
        data-testid={`citation-toggle-${citation.index}`}
      >
        <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${meta.color}`}>
          <Icon className="h-3 w-3" />
          {meta.label}
        </span>
        <div className="min-w-0 flex-1 flex items-center gap-1.5">
          <div className="text-xs text-slate-100 truncate font-medium">{label}</div>
          {meta_entity_type && (
            <Badge className="text-[9px] bg-[#131C3A] text-slate-400 border-slate-700">
              {meta_entity_type}
            </Badge>
          )}
        </div>
        <span className="inline-flex items-center rounded bg-rose-950/60 border border-rose-800/50 px-1 text-[10px] text-rose-200 shrink-0">
          [{citation.index}]
        </span>
        {excerpt && (
          expanded
            ? <ChevronUp className="h-3 w-3 text-slate-500 shrink-0" />
            : <ChevronDown className="h-3 w-3 text-slate-500 shrink-0" />
        )}
      </button>

      {expanded && excerpt && (
        <div className="px-2 pb-2 pt-0 space-y-2" data-testid={`citation-expanded-${citation.index}`}>
          <div className="rounded bg-[#0A1024] border border-[#1F2A4D] p-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Exact quote{citation.chunk_index !== undefined ? ` · chunk #${citation.chunk_index}` : ''}
              {citation.score !== undefined && ` · score ${citation.score.toFixed(2)}`}
            </div>
            <p className="text-[11px] text-slate-300 leading-relaxed whitespace-pre-wrap">
              {highlighted}
            </p>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
              {meta_signal && (
                <Badge className="text-[9px] bg-emerald-950/60 text-emerald-200 border-emerald-800/40">
                  Matched by Signal
                </Badge>
              )}
              {meta_url && (
                <a
                  href={meta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-slate-400 hover:text-rose-300 transition"
                  data-testid={`citation-url-${citation.index}`}
                >
                  <ExternalLink className="h-2.5 w-2.5" />
                  original
                </a>
              )}
            </div>
            {onOpen && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpen(citation); }}
                className="inline-flex items-center gap-1 text-[10px] text-rose-300 hover:text-rose-200 transition"
                data-testid={`citation-open-${citation.index}`}
              >
                Open source
                <ArrowRight className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CopilotCitationCard;
